import { Maximize, Minimize } from 'lucide-react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import { useAppSelector } from '../../../hooks/redux';
import { declOfNum } from '../../../utils/decOfNum';
import { Button, type IconProps, CloseButton, Heading, Pressable } from '../../../shared/ui';
import { Love } from '../../icons/reactions/love';
import { Joy } from '../../icons/reactions/joy';
import { Sadness } from '../../icons/reactions/sadness';
import { Fear } from '../../icons/reactions/fear';
import { Anger } from '../../icons/reactions/anger';

const EMOTION_ORDER = ['Любовь', 'Радость', 'Грусть', 'Страх', 'Гнев'] as const;

const emotionIcons: Record<string, FC<IconProps>> = {
  Любовь: Love,
  Радость: Joy,
  Грусть: Sadness,
  Страх: Fear,
  Гнев: Anger,
};

export interface EmotionsGroupPsychologistResultsProps {
  expectedParticipantIds: string[];
  participantDisplayNames?: Record<string, string>;
  onClose: () => void;
  /** Панель развёрнута на всю высоту поверх видео */
  isExpanded?: boolean;
  /** Переключить развёрнутое состояние */
  onExpandToggle?: () => void;
  /** Вызывается при переключении таба: номер участника и его эмоции — для трансляции клиентам */
  onActiveTabChange?: (participantLabel: string, emo: string[]) => void;
}

const GROUP_EMOTIONS_INSTRUCTIONS = [
  { title: 'Как это работает:', lines: ['Клиенты видят список категорий эмоций и выбирают подходящие.'] },
  { title: 'Завершение работы:', lines: ['Нажмите Х (крестик).'] },
];

/**
 * Нижняя панель психолога в групповой терапии после «Запросить»:
 * Счётчик ответивших, «Клиенты выбирают…», затем табы с результатами по участникам.
 */
export const EmotionsGroupPsychologistResults: FC<EmotionsGroupPsychologistResultsProps> = ({
  expectedParticipantIds,
  participantDisplayNames,
  onClose,
  isExpanded = false,
  onExpandToggle,
  onActiveTabChange,
}) => {
  const clientSelectionsByParticipant = useAppSelector(
    (state) => state.emotionsGroupSlice.clientSelectionsByParticipant,
  );
  const emotionSettings = useAppSelector(
    (state) => state.psyToolsSlice.tools.find((el) => el.name === 'emotions_detection')?.psy_tool_settings,
  );

  const respondedIds = expectedParticipantIds.filter((id) => clientSelectionsByParticipant[id]?.emo?.length > 0);
  const allResponded = expectedParticipantIds.length > 0 && respondedIds.length >= expectedParticipantIds.length;
  const [forceShowResults, setForceShowResults] = useState(false);
  const canShowResults = allResponded || forceShowResults;

  const [activeTabId, setActiveTabId] = useState<string | null>(null);

  const effectiveActiveId = activeTabId && respondedIds.includes(activeTabId) ? activeTabId : (respondedIds[0] ?? null);

  const participantNumber = (index: number) => index + 1;

  const initialTabSyncDone = useRef(false);
  useEffect(() => {
    if (!canShowResults || respondedIds.length === 0 || !onActiveTabChange) return;
    if (initialTabSyncDone.current) return;
    initialTabSyncDone.current = true;
    const firstId = respondedIds[0];
    setActiveTabId(firstId);
    const idx = expectedParticipantIds.indexOf(firstId);
    const numLabel = String(participantNumber(idx >= 0 ? idx : 0));
    const labelForClient = participantDisplayNames?.[firstId] ?? numLabel;
    const emo = clientSelectionsByParticipant[firstId]?.emo ?? [];
    onActiveTabChange(labelForClient, emo);
  }, [
    canShowResults,
    respondedIds,
    expectedParticipantIds,
    clientSelectionsByParticipant,
    participantDisplayNames,
    onActiveTabChange,
  ]);

  const activeSelection = effectiveActiveId ? clientSelectionsByParticipant[effectiveActiveId] : null;
  const activeEmo = activeSelection?.emo ?? [];

  /** Группы эмоций по категориям (как в терапевтическом журнале): слева смайлик + название, справа теги */
  const emotionsByGroup = useMemo(() => {
    if (!activeSelection?.category?.length || !activeSelection.emo?.length || !emotionSettings?.length) {
      return [];
    }
    const emoSet = new Set(activeSelection.emo);
    const result: { category: string; items: string[] }[] = [];
    for (const cat of EMOTION_ORDER) {
      if (!activeSelection.category.includes(cat)) continue;
      const items = (emotionSettings.find((s) => s.name === cat)?.value ?? []).filter((e) => emoSet.has(e));
      if (items.length > 0) result.push({ category: cat, items });
    }
    return result;
  }, [activeSelection?.category, activeSelection?.emo, emotionSettings]);

  return (
    <div className="relative flex flex-col h-full min-h-0 bg-white rounded-chip md:rounded-card overflow-hidden">
      <div className="absolute top-5 right-5 flex items-center gap-2 z-[40] pointer-events-auto">
        {onExpandToggle && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="pointer-events-auto"
            aria-label={isExpanded ? 'Свернуть панель' : 'Расширить панель'}
            startIcon={isExpanded ? <Minimize size={16} aria-hidden /> : <Maximize size={16} aria-hidden />}
            onClick={onExpandToggle}
          >
            {isExpanded ? 'Свернуть' : 'Расширить'}
          </Button>
        )}
        <CloseButton className="pointer-events-auto" onClick={onClose} />
      </div>
      <div
        className={classNames(
          'relative flex items-center gap-3 flex-shrink-0 p-4 pb-2',
          !canShowResults && 'border-b border-line',
        )}
      >
        <div className="flex items-baseline gap-3 min-w-0 flex-1 flex-wrap">
          <Heading size="lg" className="shrink-0">
            Детектор эмоций
          </Heading>
          {canShowResults && (
            <p className="text-ink-muted text-body-xl truncate min-w-0">
              Клиенты сделали выбор. Выберите участника, чтобы посмотреть результат.
            </p>
          )}
        </div>
        {!canShowResults && expectedParticipantIds.length > 0 && (
          <div className="flex items-center gap-3 shrink-0">
            <p className="text-ink text-body-xl-strong tabular-nums">
              Ответили: {respondedIds.length} из {expectedParticipantIds.length}
            </p>
            {respondedIds.length > 0 && (
              <Button size="sm" variant="primary" onClick={() => setForceShowResults(true)}>
                Показать результаты
              </Button>
            )}
          </div>
        )}
        <div className="flex-1 min-w-0" aria-hidden />
      </div>

      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {!canShowResults ? (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-4 white-scroll">
              <div className="flex flex-col gap-5 text-ink text-body-xl">
                {GROUP_EMOTIONS_INSTRUCTIONS.map((block, i) => (
                  <div key={i}>
                    <p className="mb-1.5 text-heading-md">{block.title}</p>
                    <ul className="list-disc list-inside space-y-1 text-ink-muted text-body-xl">
                      {block.lines.map((line, j) => (
                        <li key={j}>{line}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0 px-4 pb-4 flex flex-col items-center gap-2">
              <span className="inline-flex items-center px-10 py-2 rounded-control bg-primary/50 text-white text-body-xl-strong">
                Клиенты выбирают...
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 pb-3 mb-3 shrink-0 px-4">
              {respondedIds.map((id) => {
                const idx = expectedParticipantIds.indexOf(id);
                return (
                  <Pressable
                    key={id}
                    onClick={() => {
                      setActiveTabId(id);
                      const numLabel = String(participantNumber(idx >= 0 ? idx : 0));
                      const labelForClient = participantDisplayNames?.[id] ?? numLabel;
                      const emo = clientSelectionsByParticipant[id]?.emo ?? [];
                      onActiveTabChange?.(labelForClient, emo);
                    }}
                    title={participantDisplayNames?.[id]}
                    aria-pressed={effectiveActiveId === id}
                    className={classNames(
                      'flex items-center justify-center w-9 h-9 rounded-full text-body-xl-strong transition-colors',
                      effectiveActiveId === id
                        ? 'bg-primary text-white border-2 border-white'
                        : 'bg-line text-ink hover:bg-line-strong',
                    )}
                  >
                    {participantNumber(idx >= 0 ? idx : 0)}
                  </Pressable>
                );
              })}
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 pb-4 white-scroll">
              {activeEmo.length > 0 ? (
                emotionsByGroup.length > 0 ? (
                  <div className="flex flex-col gap-4 w-full">
                    {emotionsByGroup.map((group) => {
                      const IconComponent = emotionIcons[group.category];
                      const count = group.items.length;

                      return (
                        <div
                          key={group.category}
                          className="flex flex-col sm:flex-row gap-3 sm:gap-4 p-4 rounded-control bg-white shadow-raised border border-line min-w-0"
                        >
                          <div className="flex items-center gap-3 shrink-0">
                            <div className="w-[80px] h-[80px] min-w-[80px] min-h-[80px] bg-surface-muted rounded-control bg-surface-muted flex items-center justify-center">
                              {IconComponent ? (
                                <IconComponent
                                  needHover={false}
                                  stroke="#63656A"
                                  className="w-[50px] h-[50px] !cursor-default"
                                />
                              ) : null}
                            </div>
                            <div className="flex flex-col">
                              <p className="text-ink text-heading-md">{group.category}</p>
                              <p className="text-ink-muted text-ui">
                                {count} {declOfNum(count, ['эмоция', 'эмоции', 'эмоций'])}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 items-center min-w-0 flex-1">
                            {group.items.map((item, itemIdx) => (
                              <span
                                key={`${group.category}-${itemIdx}`}
                                className="bg-line text-ink px-3 py-1.5 rounded-chip text-ui-strong"
                              >
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex gap-[15px] flex-wrap w-full">
                    {activeEmo.map((el, idx) => (
                      <div
                        className="bg-line rounded-chip py-2 px-2 lg:py-[5px] lg:px-[10px] text-body-l-strong text-ink"
                        key={idx}
                      >
                        {el}
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <p className="text-ink-muted text-body-xl">
                  {respondedIds.includes(effectiveActiveId ?? '') ? 'Нет выбранных эмоций' : 'Участник ещё не ответил'}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
